import React, { useState, useEffect } from 'react';
import { Phone, Wrench, Coffee, LayoutGrid, Sparkles, Cloud, FileText, Users, ClipboardList, Shield, CheckCircle, MapPin, Calendar, Bookmark, User, Calculator, Share2, ListMusic, Zap, List, Activity, Monitor } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ICON_MAP = {
  phone: Phone,
  wrench: Wrench,
  coffee: Coffee,
  layout: LayoutGrid,
  cloud: Cloud,
  'file-text': FileText,
  users: Users,
  clipboard: ClipboardList,
  'clipboard-list': ClipboardList,
  shield: Shield,
  'check-circle': CheckCircle,
  'map-pin': MapPin,
  calendar: Calendar,
  bookmark: Bookmark,
  user: User,
  calculator: Calculator,
  share: Share2,
  'list-music': ListMusic,
  zap: Zap,
  list: List,
  activity: Activity,
  monitor: Monitor,
};

const STATUS_CONFIG = {
  new: { label: 'NEW', bg: 'rgba(52,199,89,0.12)', color: '#34C759', border: 'rgba(52,199,89,0.25)' },
  feature: { label: 'FEATURE', bg: 'rgba(100,160,220,0.10)', color: '#7CB3E0', border: 'rgba(100,160,220,0.2)' },
  update: { label: 'UPDATE', bg: 'rgba(200,146,62,0.12)', color: 'var(--amber)', border: 'rgba(200,146,62,0.25)' },
};

function Card({ item }) {
  const Icon = ICON_MAP[item.icon] || Sparkles;
  const cfg = STATUS_CONFIG[item.status] || STATUS_CONFIG.update;

  return (
    <div
      className="rounded-xl p-5 border transition-all hover:border-[var(--amber-dim)]"
      style={{ background: 'var(--card)', borderColor: 'var(--lift)' }}
      data-testid={`whats-new-card-${item.id}`}
    >
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0" style={{ background: 'var(--card-2)' }}>
          <Icon className="h-4 w-4 text-[var(--amber)]" />
        </div>
        <div className="min-w-0">
          <span
            className="inline-block text-[9px] font-semibold uppercase tracking-widest px-2 py-0.5 rounded mb-1.5"
            style={{ background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}` }}
          >
            {cfg.label}
          </span>
          <h3 className="text-[var(--t0)] font-semibold text-sm leading-snug">{item.headline}</h3>
          <p className="text-[var(--t2)] text-xs mt-1 leading-relaxed line-clamp-3">{item.description}</p>
        </div>
      </div>
    </div>
  );
}

export default function WhatsNewPage() {
  const [items, setItems] = useState([]);
  const [error, setError] = useState(false);

  useEffect(() => {
    fetch(`${API_URL}/api/whats-new`)
      .then(r => { if (!r.ok) throw new Error('not ok'); return r.json(); })
      .then(data => {
        if (!Array.isArray(data)) throw new Error('unexpected response shape');
        setItems(data);
      })
      .catch(() => setError(true));
  }, []);

  const newItems = items.filter(i => i.status === 'new');
  const featureItems = items.filter(i => i.status === 'feature');
  const updateItems = items.filter(i => i.status === 'update');

  return (
    <div className="whats-new-page">
      {/* Header */}
      <div className="flex items-center gap-2.5 mb-3">
        <Sparkles className="h-5 w-5 text-[var(--amber)]" />
        <h1 className="font-['Barlow_Condensed'] font-bold text-2xl text-[var(--t0)] uppercase tracking-wide">What's New</h1>
      </div>
      <p className="text-[var(--t2)] text-sm leading-relaxed max-w-2xl mb-10" style={{ fontFamily: 'var(--font-body)' }}>
        STAGE8 is built to reduce uncertainty in live event coordination. Here's what's changed recently.
      </p>

      {/* Error state — shown instead of white-screening on a bad/failed response */}
      {error && (
        <div
          className="rounded-xl p-5 border text-sm"
          style={{ background: 'var(--card)', borderColor: 'var(--lift)', color: 'var(--t2)' }}
          data-testid="whats-new-error"
        >
          We couldn't load the latest updates right now. Please try again later.
        </div>
      )}

      {/* New section */}
      {newItems.length > 0 && (
        <section className="mb-10" data-testid="section-new">
          <div className="flex items-center gap-2 mb-4">
            <div className="h-1.5 w-1.5 rounded-full" style={{ background: '#34C759' }} />
            <h2 className="text-xs font-semibold uppercase tracking-widest" style={{ fontFamily: 'var(--font-ui)', color: '#34C759' }}>Recently Added</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {newItems.map(item => <Card key={item.id} item={item} />)}
          </div>
        </section>
      )}

      {/* Features section */}
      {featureItems.length > 0 && (
        <section className="mb-10" data-testid="section-features">
          <div className="flex items-center gap-2 mb-4">
            <div className="h-1.5 w-1.5 rounded-full" style={{ background: '#7CB3E0' }} />
            <h2 className="text-xs font-semibold uppercase tracking-widest" style={{ fontFamily: 'var(--font-ui)', color: '#7CB3E0' }}>Core Features</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {featureItems.map(item => <Card key={item.id} item={item} />)}
          </div>
        </section>
      )}

      {/* Updates section */}
      {updateItems.length > 0 && (
        <section className="mb-10" data-testid="section-updates">
          <div className="flex items-center gap-2 mb-4">
            <div className="h-1.5 w-1.5 rounded-full" style={{ background: 'var(--amber)' }} />
            <h2 className="text-xs font-semibold uppercase tracking-widest" style={{ fontFamily: 'var(--font-ui)', color: 'var(--amber)' }}>Improvements</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {updateItems.map(item => <Card key={item.id} item={item} />)}
          </div>
        </section>
      )}
    </div>
  );
}
